/**
 * customer-engraving-page.js
 *
 * Bootstraps the engraving editor on the standalone "pages/edit-engraving"
 * page. Reached via the Customer Account UI Extension navigating to:
 *   /pages/edit-engraving?id=<engraving-record-id>&token=<one-time>&return=<account-url>
 *
 * A SECOND FLOW lives here (SOS 6ad1c399): ?mode=add&order=<id>&line=<id>&token=<add token>
 * — a customer puts an engraving on a bottle they ordered plain, and pays for it. See
 * the "ADD MODE" block below; the edit flow that follows is described first.
 *
 * Flow:
 *   1. Parse URL params (id, token, return).
 *   2. GET <apiBase>/<id>?token=<token> → full engraving record + productHandle.
 *   3. GET /<locale>/products/<handle>?view=engraving-vue → productConfig.
 *   4. window.createEngravingApp(...) with mode: 'edit' + editContext (prefill,
 *      allowed modes, save callback).
 *   5. On save → POST <apiBase>/<id>?token=<token> → on success redirect to
 *      `return` URL. On error show inline banner.
 */

(function () {
  'use strict';

  var cfg = window.__engravingEditPageConfig;
  if (!cfg) {
    console.error('[engraving-edit-page] missing __engravingEditPageConfig');
    return;
  }

  var params = new URLSearchParams(window.location.search);
  var engravingId = params.get('id');
  var editToken = params.get('token');
  var returnUrl = params.get('return') || cfg.defaultReturnUrl || 'https://account.snowmonkey.com';

  var mountEl = document.getElementById('engraving-edit-app');

  // The editor's modal is `<Teleport to="body">`d out of the mount element
  // (engraving-app/src/App.vue), so emptying #engraving-edit-app leaves the
  // full-screen overlay behind, covering whatever we render next. Only
  // `app.unmount()` takes the teleported node with it — hence the handle.
  var engravingApp = null;

  // Set once the confirmation card is up. The editor calls onSaved() and then
  // closeModal() unconditionally (engraving-app/src/App.vue, runEditSave), and
  // closeModal() fires the host's onClose — so without this a successful save
  // redirects a beat after the card renders and the customer sees the
  // confirmation flash past. Leaving is the customer's call, via the card's
  // own button.
  var savedShown = false;

  function showError(message) {
    var bar = document.createElement('div');
    bar.className = 'engraving-edit-page-error';
    bar.textContent = message;
    var link = document.createElement('a');
    link.href = returnUrl;
    link.textContent = cfg.i18n.backToAccount;
    bar.appendChild(link);
    document.body.appendChild(bar);
    if (mountEl) mountEl.innerHTML = '';
  }

  function redirectToReturn() {
    window.location.href = returnUrl;
  }

  /**
   * The `return` URL, flagged so the customer-account order page knows a save
   * just landed. The "Edit engraving" extension block reads this and repeats
   * the confirmation there — right next to the original line-item text it is
   * explaining away.
   */
  function returnUrlWithSavedFlag() {
    try {
      var u = new URL(returnUrl, window.location.origin);
      u.searchParams.set('engraving_saved', '1');
      return u.toString();
    } catch (e) {
      return returnUrl + (returnUrl.indexOf('?') >= 0 ? '&' : '?') + 'engraving_saved=1';
    }
  }

  /**
   * Replaces the editor with a card — a confirmation, or the way on to payment —
   * instead of bouncing straight back to the order.
   *
   * The bounce is what made this necessary: Shopify freezes a line item's
   * engraving properties at checkout and gives us no way to rewrite them, so
   * the order page the customer lands back on still prints the text they had
   * *before* the edit. Landing there with no confirmation reads as "my change
   * was lost", and support gets the ticket. Say it here, plainly, first.
   *
   * Copy is set with textContent, never innerHTML — these strings come from
   * the theme's locale files (or, for the link, from the server) and must never
   * be parsed as markup.
   *
   * @param {{title: string, body?: string, note?: string, buttonLabel: string, href: string}} opts
   */
  function showMessageCard(opts) {
    savedShown = true;
    if (engravingApp && engravingApp.app && typeof engravingApp.app.unmount === 'function') {
      try {
        engravingApp.app.unmount();
      } catch (e) {
        console.error('[engraving-edit-page] unmount failed', e);
      }
      engravingApp = null;
    }
    document.body.classList.remove('modal-open');

    var card = document.createElement('div');
    card.className = 'engraving-saved__card';

    var icon = document.createElement('div');
    icon.className = 'engraving-saved__icon';
    icon.textContent = '\u2713';
    card.appendChild(icon);

    var title = document.createElement('h1');
    title.className = 'engraving-saved__title';
    title.textContent = opts.title;
    card.appendChild(title);

    if (opts.body) {
      var body = document.createElement('p');
      body.className = 'engraving-saved__body';
      body.textContent = opts.body;
      card.appendChild(body);
    }

    if (opts.note) {
      var note = document.createElement('div');
      note.className = 'engraving-saved__note';
      note.textContent = opts.note;
      card.appendChild(note);
    }

    var link = document.createElement('a');
    link.className = 'engraving-saved__button';
    link.href = opts.href;
    link.textContent = opts.buttonLabel;
    card.appendChild(link);

    var wrap = document.createElement('div');
    wrap.className = 'engraving-saved';
    wrap.appendChild(card);

    if (mountEl) {
      mountEl.innerHTML = '';
      mountEl.appendChild(wrap);
    } else {
      document.body.appendChild(wrap);
    }
    window.scrollTo(0, 0);
  }

  function showSaved() {
    var i18n = cfg.i18n || {};
    showMessageCard({
      title: i18n.savedTitle || 'Engraving saved',
      body: i18n.savedBody,
      note: i18n.savedNote,
      buttonLabel: i18n.backToOrder || i18n.backToAccount || 'Back',
      href: returnUrlWithSavedFlag(),
    });
  }

  /* =========================================================================
     ADD MODE (SOS 6ad1c399)
       ?mode=add&order=<order id>&line=<line item id>&token=<add token>&return=<account url>

     A customer puts an engraving on a bottle they ordered plain, and pays for it.
     The Customer Account block issued a token for THAT bottle; this page loads
     the draft with it (the bottle, both prices, the designs on sale), opens the
     same editor the admin and the storefront use, and on save asks the server
     to put the fee on the order and write the engraving down. The server answers
     with Shopify's own payment page, and that is where the customer goes.

     THREE THINGS THIS PAGE DOES NOT DO:
       - it does not decide a price. The server quotes both kinds and charges what it
         quoted; the page only says them, in the customer's currency.
       - it does not follow a link it was not sure of. The payment link is data from
         a server, and a page that follows whatever it is told is an open redirect:
         only https on the shop's own domains.
       - it does not show a sentence a server wrote. The server says a code; the
         words are this page's own, in the customer's language (the Liquid page
         renders them), and an engraving the editor itself rejects is the editor's.
     ========================================================================= */

  // How a refusal code is worded. A group not named here is "Unavailable".
  var ADD_ERROR_GROUP = {
    ORDER_PROCESSED: 'Closed', ORDER_PACKING: 'Closed', ORDER_SHIPPED: 'Closed', ORDER_CANCELLED: 'Closed',
    ORDER_MIGRATED: 'Closed', NOT_EDITABLE: 'Closed',
    PAYMENT_PENDING: 'Pending', IN_PROGRESS: 'Pending',
    TEMPORARILY_UNAVAILABLE: 'Retry', TRY_AGAIN: 'Retry', PRICE_UNAVAILABLE: 'Retry',
    CURRENCY_MISMATCH: 'Retry', NO_MARKET: 'Retry',
  };

  function addErrorText(code, status) {
    var i18n = cfg.i18n || {};
    if (status === 401) return i18n.addErrorExpired || i18n.saveError;
    if (code === 'INVALID_ENGRAVING') return i18n.saveError;
    return i18n['addError' + (ADD_ERROR_GROUP[code] || 'Unavailable')] || i18n.saveError;
  }

  /** "Text {{text}}" with {text: '4,90 €'}. Unknown names are left out, not printed. */
  function fill(template, values) {
    return String(template || '').replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, key) {
      return values[key] != null ? values[key] : '';
    });
  }

  function formatMoney(amount, currency) {
    try {
      return new Intl.NumberFormat(cfg.locale || undefined, { style: 'currency', currency: currency }).format(amount);
    } catch (e) {
      return Number(amount).toFixed(2) + ' ' + currency;
    }
  }

  /**
   * What the engraving costs, for the notice above the form. TOTALS for the bottle —
   * a bottle ordered three times is engraved three times — and, when that is so,
   * the sentence that says it applies to every piece.
   */
  function priceNotice(draft) {
    var i18n = cfg.i18n || {};
    var currency = draft.currency || 'EUR';
    var fees = draft.fees || {};
    var text = fees.text ? formatMoney(fees.text.total, currency) : '';
    var symbol = fees.symbol ? formatMoney(fees.symbol.total, currency) : '';
    var notice = fill(i18n.addPriceNotice, { text: text, symbol: symbol });
    if (draft.quantity > 1) notice += ' ' + fill(i18n.addQuantityNotice, { count: draft.quantity });
    return notice;
  }

  /** Only Shopify's own pages: https, on the shop's domains (or a subdomain of one). */
  function isPayUrlSafe(value) {
    try {
      var url = new URL(value);
      if (url.protocol !== 'https:') return false;
      var hosts = cfg.payHosts || [];
      for (var i = 0; i < hosts.length; i += 1) {
        var host = hosts[i];
        if (host && (url.hostname === host || url.hostname.slice(-(host.length + 1)) === '.' + host)) return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  }

  var addEndpoint = '';
  var addResult = null;

  function addApi(method, payload) {
    return fetch(addEndpoint, {
      method: method,
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: payload ? JSON.stringify(payload) : undefined,
    }).then(function (resp) {
      return resp.json().then(function (json) { return json; }, function () { return {}; }).then(function (json) {
        if (!resp.ok) {
          var err = new Error('HTTP ' + resp.status);
          err.code = json && json.error;
          err.status = resp.status;
          throw err;
        }
        return json;
      });
    });
  }

  /** The editor awaits this and shows a thrown error inline: the page's own words, never the server's. */
  function submitAdd(vueData) {
    return addApi('POST', { data: vueDataToBackend(vueData) }).then(function (body) {
      addResult = (body && body.data) || {};
      return body;
    }, function (err) {
      throw new Error(addErrorText(err.code, err.status));
    });
  }

  /**
   * Where the editor's save leaves the customer: on Shopify's payment page if the
   * engraving is recorded and the link is Shopify's; on their order page, with the
   * way to pay waiting there, if it is recorded and no link could be read; and on a
   * "we are finishing" card, sent to pay nothing, if it is not recorded yet.
   */
  function showAddResult() {
    var i18n = cfg.i18n || {};
    var result = addResult || {};

    if (result.recorded && result.payUrl && isPayUrlSafe(result.payUrl)) {
      showMessageCard({ title: i18n.addRedirecting, buttonLabel: i18n.addPayNow, href: result.payUrl });
      window.location.assign(result.payUrl);
      return;
    }

    if (result.recorded) {
      showMessageCard({
        title: i18n.addLaterTitle,
        body: i18n.addLaterBody,
        buttonLabel: i18n.backToOrder || i18n.backToAccount,
        href: returnUrlWithSavedFlag(),
      });
      return;
    }

    showMessageCard({
      title: i18n.addFinishingTitle,
      body: i18n.addFinishingBody,
      buttonLabel: i18n.backToOrder || i18n.backToAccount,
      href: returnUrl,
    });
  }

  function bootstrapAdd(draft) {
    if (!draft || !draft.productHandle) {
      showError(cfg.i18n.saveError || 'Missing product config');
      return;
    }

    fetchProductConfig(draft.productHandle).then(function (productConfig) {
      var addConfig = Object.assign({}, productConfig, {
        mode: 'edit',
        // The page's own words win over the fetched ones, as on the edit flow.
        i18n: Object.assign({}, productConfig.i18n, cfg.editorI18n || {}),
        catalog: draft.catalog || null,
        editContext: {
          allowedModes: draft.allowedModes,
          // Truthy prefill is what opens the editor on mount; an empty object lands on
          // its defaults — text, blank lines.
          prefillData: {},
          // A payment, said as one: the button is where they agree to it.
          saveLabel: cfg.i18n.addSave || cfg.i18n.save || '',
          downgradeNoticeText: '',
          symbolUpgradeTooltip: '',
          // The editor's one banner above the form is the one it keeps for a locked
          // order. It renders any text, and says the price there.
          lockWarning: priceNotice(draft),
          lockedText: cfg.i18n.locked || '',
          conflictText: cfg.i18n.conflict || '',
          onSave: submitAdd,
          onSaved: showAddResult,
          onClose: function () {
            // Fired by a cancel AND by the close after a save: only the cancel leaves.
            if (savedShown) return;
            redirectToReturn();
          },
        },
      });

      if (typeof window.createEngravingApp !== 'function') {
        showError('Editor unavailable');
        return;
      }

      if (mountEl) mountEl.innerHTML = '';
      engravingApp = window.createEngravingApp(mountEl, addConfig);
    }).catch(function (err) {
      console.error('[engraving-edit-page] add: productConfig fetch failed', err);
      showError(cfg.i18n.saveError || 'Failed to load editor');
    });
  }

  function startAdd() {
    var order = params.get('order') || '';
    var line = params.get('line') || '';
    if (!/^\d+$/.test(order) || !/^\d+$/.test(line) || !editToken) {
      showError(cfg.i18n.saveError || 'Missing token');
      return;
    }

    addEndpoint = cfg.apiBase + '/add/' + encodeURIComponent(order) + '/' + encodeURIComponent(line)
      + '?token=' + encodeURIComponent(editToken);

    addApi('GET').then(function (body) {
      bootstrapAdd(body && body.data);
    }, function (err) {
      showError(addErrorText(err.code, err.status));
    });
  }

  if (params.get('mode') === 'add') {
    startAdd();
    return;
  }

  if (!engravingId || !editToken) {
    showError(cfg.i18n.saveError || 'Missing token');
    return;
  }

  function buildApiUrl(suffix) {
    var sep = cfg.apiBase.indexOf('?') >= 0 ? '&' : '?';
    return cfg.apiBase + '/' + encodeURIComponent(engravingId) + suffix;
  }

  function fetchRecord() {
    var url = buildApiUrl('?token=' + encodeURIComponent(editToken));
    return fetch(url, {
      method: 'GET',
      credentials: 'omit',
      headers: { Accept: 'application/json' },
    }).then(function (resp) {
      return resp.json().then(function (body) {
        if (!resp.ok) {
          var err = new Error((body && body.message) || ('HTTP ' + resp.status));
          err.code = (body && body.error) || ('HTTP_' + resp.status);
          err.status = resp.status;
          throw err;
        }
        return body && body.data;
      });
    });
  }

  function fetchProductConfig(handle) {
    var prefix = cfg.productConfigPathPrefix || '/products/';
    var suffix = cfg.productConfigPathSuffix || '?view=engraving-vue';
    if (prefix.charAt(0) !== '/') { prefix = '/' + prefix; }
    var path = prefix + encodeURIComponent(handle) + suffix;
    var url = 'https://snowmonkey.com' + path;
    // NOTE: do NOT send Accept: application/json — Shopify intercepts that on
    // /products/<handle> and returns the standard Product JSON ({product:{...}})
    // instead of our ?view=engraving-vue template output. With the default
    // Accept header (browser-set */*), Shopify renders the alternate template.
    return fetch(url, {
      method: 'GET',
      credentials: 'omit',
    }).then(function (resp) {
      if (!resp.ok) throw new Error('productConfig HTTP ' + resp.status);
      return resp.text();
    }).then(function (text) {
      text = (text || '').trim();
      if (!text || text === '{}') throw new Error('No engraving config for product');
      return JSON.parse(text);
    });
  }

  // The mapping lives in engraving-app/src/lib/payload.js and is published on
  // window by that bundle, so this page and the admin editor can't drift apart.
  // The inline copy stays as a fallback for the (transient) case where this
  // asset loads before engraving-app.iife.js.
  function vueDataToBackend(d) {
    if (window.engravingPayload && typeof window.engravingPayload.toBackend === 'function') {
      return window.engravingPayload.toBackend(d);
    }
    return {
      engraving_mode: d.mode,
      font_family: d.font || '',
      font_size: d.fontSize,
      lines: Array.isArray(d.lines) ? d.lines : [],
      zodiac: d.selectedSymbol || '',
      zodiac_size: d.symbolSize,
      zodiac_font_family: d.zodiacFont || '',
      zodiac_font_size: d.zodiacFontSize,
      zodiac_line_1: Array.isArray(d.zodiacLines) ? (d.zodiacLines[0] || '') : '',
      zodiac_line_2: Array.isArray(d.zodiacLines) ? (d.zodiacLines[1] || '') : '',
    };
  }

  function saveRecord(vueData, expectedEditCount) {
    var url = buildApiUrl('?token=' + encodeURIComponent(editToken));
    return fetch(url, {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      // The edit_count this page loaded with. Support staff can edit the same
      // engraving from the admin (they deliberately bypass the order lock to
      // fix things at the bench), so without this a save from a page left open
      // would overwrite their correction with values from before it.
      body: JSON.stringify({
        data: vueDataToBackend(vueData),
        expected_edit_count: expectedEditCount,
      }),
    }).then(function (resp) {
      return resp.json().then(function (body) {
        if (!resp.ok) {
          var err = new Error((body && body.message) || ('HTTP ' + resp.status));
          err.code = (body && body.error) || ('HTTP_' + resp.status);
          err.status = resp.status;
          err.body = body;
          throw err;
        }
        return body;
      });
    });
  }

  function bootstrap(record) {
    if (record.locked) {
      showError(record.lockedReason || cfg.i18n.locked);
      return;
    }

    var productHandle = record.productHandle || record.product_handle;
    if (!productHandle) {
      showError(cfg.i18n.saveError || 'Missing product config');
      return;
    }

    fetchProductConfig(productHandle).then(function (productConfig) {
      var editConfig = Object.assign({}, productConfig, {
        mode: 'edit',
        // The editor's strings come from THIS page, not from the fetched config.
        //
        // fetchProductConfig asks for `/<locale>/products/<handle>`, but Shopify
        // discards the locale prefix on redirect whenever the request carries an
        // Accept-Language header — which every browser sends — so the response is
        // in the shop's default language no matter which locale the customer is
        // browsing. This page, by contrast, is rendered server-side in the right
        // language already. Layering its copy on top is what stops a Czech
        // customer editing a Czech engraving in a Slovenian modal.
        //
        // Still merged rather than replaced: the product config carries keys
        // this page does not render (pricing copy for the upsell row), and
        // losing those would blank them.
        i18n: Object.assign({}, productConfig.i18n, cfg.editorI18n || {}),
        // The admin-managed series, sent with the record rather than fetched.
        // This page builds its picker from the product's Liquid config, which
        // knows only the built-in series, and the public catalog endpoint
        // answers only with what is on sale — neither can offer the series a
        // customer already bought once it leaves the storefront. Null means the
        // database has no catalog, which keeps the Liquid config in charge.
        catalog: record.catalog || null,
        editContext: {
          engravingId: record.id,
          orderId: record.orderId || record.shopify_order_id,
          originalTier: record.originalTier,
          currentTier: record.currentTier,
          allowedModes: record.allowedModes,
          prefillData: record.current,
          downgradeNoticeText: cfg.i18n.downgradeNotice,
          symbolUpgradeTooltip: cfg.i18n.symbolRequiresUpgrade,
          saveLabel: cfg.i18n.save || '',
          // Save-failure copy. These live in THIS page's i18n block, not in the
          // per-product engraving config the editor is handed, so they have to
          // be passed in or the editor falls back to English.
          lockedText: cfg.i18n.locked || '',
          conflictText: cfg.i18n.conflict || '',
          onSave: function (vueData) {
            return saveRecord(vueData, record.editCount);
          },
          onSaved: function () {
            showSaved();
          },
          onClose: function () {
            // Fired both by an actual cancel AND by the close that follows a
            // successful save. Only the former should navigate away.
            if (savedShown) return;
            redirectToReturn();
          },
        },
      });

      if (typeof window.createEngravingApp !== 'function') {
        showError('Editor unavailable');
        return;
      }

      if (mountEl) mountEl.innerHTML = '';
      engravingApp = window.createEngravingApp(mountEl, editConfig);
    }).catch(function (err) {
      console.error('[engraving-edit-page] productConfig fetch failed', err);
      showError(cfg.i18n.saveError || 'Failed to load editor');
    });
  }

  fetchRecord().then(bootstrap).catch(function (err) {
    console.error('[engraving-edit-page] record fetch failed', err);
    if (err.code === 'LOCKED') {
      showError(cfg.i18n.locked || 'Order is locked');
    } else if (err.status === 401) {
      showError('This editing link has expired. Please request a new one from your account.');
    } else if (err.status === 403) {
      showError('You do not have permission to edit this engraving.');
    } else {
      showError(cfg.i18n.saveError || 'Failed to load engraving');
    }
  });
})();
